// Explore's "Add to dashboard" (plan step 3.5, D98; Console D, APS-317): the SQL that just ran
// becomes a panel on a board of the project, or on a new one, with a title and a chart to start
// from (its columns decide the axes; the board's editor changes them). Saving runs nothing. A
// window under the results' head (the mock's .win), opened by its button there; the page says
// where the panel went.
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useId, useState } from 'react'
import { dashboardQuery, dashboardsQuery, useAddPanel, useCreateDashboard } from '../api/queries'
import type { Dashboard, QueryResult } from '../api/types'
import { type MessageId, useI18n } from '../i18n'
import {
  CHART_TYPES,
  type ChartType,
  dashboardError,
  guessChart,
  isNumericType,
  LIMITS,
  nextLayout,
} from '../lib/panels'
import { useProjectParams } from '../routes/params'
import { Btn } from './d'

const NEW = '__new'

export function AddToDashboard({
  id,
  sql,
  columns,
  title: initialTitle,
  onCancel,
  onAdded,
}: {
  id: string
  sql: string
  columns: QueryResult['columns']
  /** The example's name when one ran: a title to start from. */
  title: string
  onCancel: () => void
  onAdded: (board: Dashboard) => void
}) {
  const { t } = useI18n()
  const { projectId } = useProjectParams()
  const qc = useQueryClient()
  const boards = useQuery(dashboardsQuery(projectId))
  const create = useCreateDashboard(projectId)
  const add = useAddPanel(projectId)
  const [board, setBoard] = useState('')
  const [name, setName] = useState('')
  const [title, setTitle] = useState(initialTitle)
  const [type, setType] = useState<ChartType>(() =>
    columns.length > 1 && columns.some((c) => isNumericType(c.type)) ? 'line' : 'table',
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [invalid, setInvalid] = useState(false)
  const ids = { form: useId(), board: useId(), name: useId(), title: useId(), type: useId() }

  const list = boards.data ?? []
  const target = board || (list[0]?.id ?? NEW)
  const isNew = target === NEW

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    const ok =
      title.trim().length > 0 &&
      title.trim().length <= LIMITS.title &&
      sql.length <= LIMITS.sql &&
      (!isNew || (name.trim().length > 0 && name.trim().length <= LIMITS.title))
    setInvalid(!ok)
    if (!ok || busy) return
    setBusy(true)
    setError(null)
    try {
      const into = isNew ? await create.mutateAsync(name.trim()) : list.find((b) => b.id === target)
      if (!into) return
      const { panels } = await qc.fetchQuery(dashboardQuery(projectId, into.id))
      await add.mutateAsync({
        dashboardId: into.id,
        panel: {
          kind: 'sql',
          title: title.trim(),
          sql,
          chart: guessChart(columns, type),
          layout: nextLayout(panels),
        },
      })
      onAdded(into)
    } catch (caught) {
      setError(caught)
      setBusy(false)
    }
  }

  return (
    <form id={id} className="win ex-add" aria-labelledby={ids.form} onSubmit={submit} noValidate>
      <div className="win-bar">
        <b id={ids.form}>{t('dash.add.button')}</b>
        <span>{t('dash.add.sub')}</span>
      </div>
      <div className="ex-add-b">
        <div className="ex-add-f">
          <div className="field">
            <label htmlFor={ids.board}>{t('dash.add.board')}</label>
            <select
              id={ids.board}
              className="select"
              value={target}
              disabled={boards.isPending}
              onChange={(e) => setBoard(e.target.value)}
            >
              {list.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
              {list.length < LIMITS.dashboards ? (
                <option value={NEW}>{t('dash.add.newBoard')}</option>
              ) : null}
            </select>
          </div>
          {isNew ? (
            <div className="field">
              <label htmlFor={ids.name}>{t('dash.name')}</label>
              <input
                id={ids.name}
                className="input"
                value={name}
                aria-invalid={(invalid && !name.trim()) || undefined}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          ) : null}
          <div className="field">
            <label htmlFor={ids.title}>{t('dash.f.title')}</label>
            <input
              id={ids.title}
              className="input"
              value={title}
              aria-invalid={(invalid && !title.trim()) || undefined}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor={ids.type}>{t('dash.f.chart')}</label>
            <select
              id={ids.type}
              className="select"
              value={type}
              onChange={(e) => setType(e.target.value as ChartType)}
            >
              {CHART_TYPES.map((c) => (
                <option key={c} value={c}>
                  {t(`dash.chart.${c}` as MessageId)}
                </option>
              ))}
            </select>
          </div>
        </div>
        {invalid ? (
          <p role="alert" className="notice error">
            {t('dash.add.invalid', { max: LIMITS.title })}
          </p>
        ) : null}
        {error || boards.isError ? (
          <p role="alert" className="notice error">
            {dashboardError(t, error ?? boards.error)}
          </p>
        ) : null}
        <div className="ex-add-acts">
          <Btn type="submit" variant="solid" disabled={busy}>
            {t('dash.add.save')}
          </Btn>
          <Btn onClick={onCancel}>{t('settings.cancel')}</Btn>
        </div>
      </div>
    </form>
  )
}
